import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  Loader2,
  TestTube,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { stripeService } from '../services/stripeService'
import type {
  StripeConnectionStep,
  StripeCredentials,
} from '../types/stripe.types'
import { STRIPE_WEBHOOK_EVENTS } from '../types/stripe.types'

interface StripeConnectionDrawerProps {
  trigger: React.ReactNode
  onConnectionComplete?: () => void
}

export function StripeConnectionDrawer({
  trigger,
  onConnectionComplete,
}: StripeConnectionDrawerProps) {
  const [open, setOpen] = useState(false)
  const [currentStep, setCurrentStep] =
    useState<StripeConnectionStep>('credentials')
  const [loading, setLoading] = useState(false)

  // Credentials step
  const [credentials, setCredentials] = useState<StripeCredentials>({
    secretKey: '',
    environment: 'test',
  })
  const [showApiKey, setShowApiKey] = useState(false)
  const [validationResult, setValidationResult] = useState<{
    valid?: boolean
    error?: string
    // biome-ignore lint/suspicious/noExplicitAny: required for dynamic Stripe account info
    accountInfo?: any
  }>({})

  // Webhook step
  const [webhookUrl, setWebhookUrl] = useState('')
  const [endpointSecret, setEndpointSecret] = useState('')
  const [selectedEvents, setSelectedEvents] = useState<string[]>([
    'payment_intent.succeeded',
    'customer.created',
    'invoice.payment_succeeded',
  ])

  // Verification step
  const [webhookTestResult, setWebhookTestResult] = useState<{
    success?: boolean
    error?: string
  }>({})

  useEffect(() => {
    if (open && currentStep === 'webhook' && !webhookUrl) {
      setWebhookUrl(stripeService.generateWebhookUrl())
      setEndpointSecret(stripeService.generateEndpointSecret())
    }
  }, [open, currentStep, webhookUrl])

  const resetDrawer = () => {
    setCurrentStep('credentials')
    setCredentials({ secretKey: '', environment: 'test' })
    setValidationResult({})
    setWebhookTestResult({})
    setWebhookUrl('')
    setEndpointSecret('')
  }

  const handleCredentialsValidation = async () => {
    if (!credentials.secretKey) return

    setLoading(true)
    try {
      const result = await stripeService.validateCredentials(credentials)
      setValidationResult(result)

      if (result.valid) {
        setTimeout(() => setCurrentStep('webhook'), 1000)
      }
    } catch (_error) {
      setValidationResult({
        valid: false,
        error: 'Failed to validate credentials',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleWebhookTest = async () => {
    setLoading(true)
    try {
      const result = await stripeService.testWebhook(webhookUrl)
      setWebhookTestResult(result)

      if (result.success) {
        setTimeout(() => setCurrentStep('verification'), 1000)
      }
    } catch (_error) {
      setWebhookTestResult({
        success: false,
        error: 'Failed to test webhook endpoint',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCompleteConnection = async () => {
    setLoading(true)
    try {
      await stripeService.saveConnection({
        isConnected: true,
        accountId: validationResult.accountInfo?.id,
        accountName: validationResult.accountInfo?.name,
        environment: credentials.environment,
        webhookEndpoint: webhookUrl,
        connectedAt: new Date().toISOString(),
      })

      setCurrentStep('complete')
      onConnectionComplete?.()
      setTimeout(() => {
        setOpen(false)
        resetDrawer()
      }, 2000)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const toggleEventSelection = (event: string) => {
    setSelectedEvents(prev =>
      prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]
    )
  }

  const renderCredentialsStep = () => (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-semibold mb-2'>
          Connect Your Stripe Account
        </h3>
        <p className='text-sm text-muted-foreground'>
          Enter your Stripe API credentials to connect your account to Coobird.
        </p>
      </div>

      <div className='grid grid-cols-2 gap-4'>
        <div className='space-y-2'>
          <Label htmlFor='environment'>Environment</Label>
          <Select
            value={credentials.environment}
            onValueChange={(value: 'test' | 'live') =>
              setCredentials(prev => ({ ...prev, environment: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='test'>Test</SelectItem>
              <SelectItem value='live'>Live</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className='flex items-end'>
          <Badge variant='outline' className='mb-2'>
            {credentials.environment === 'test'
              ? '🧪 Safe for testing'
              : '🚀 Production ready'}
          </Badge>
        </div>
      </div>

      <div className='space-y-2'>
        <Label htmlFor='api-key'>Stripe Secret Key *</Label>
        <div className='relative'>
          <Input
            id='api-key'
            type={showApiKey ? 'text' : 'password'}
            value={credentials.secretKey}
            onChange={e =>
              setCredentials(prev => ({ ...prev, secretKey: e.target.value }))
            }
            placeholder={`sk_${credentials.environment}_...`}
            className='pr-10'
          />
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent'
            onClick={() => setShowApiKey(!showApiKey)}
          >
            {showApiKey ? (
              <EyeOff className='h-4 w-4' />
            ) : (
              <Eye className='h-4 w-4' />
            )}
          </Button>
        </div>
        <p className='text-xs text-muted-foreground'>
          Find your API key in the{' '}
          <a
            href='https://dashboard.stripe.com/apikeys'
            target='_blank'
            rel='noopener noreferrer'
            className='text-blue-600 hover:underline inline-flex items-center gap-1'
          >
            Stripe Dashboard <ExternalLink className='h-3 w-3' />
          </a>
        </p>
      </div>

      {validationResult.error && (
        <div className='flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm'>
          <AlertCircle className='h-4 w-4' />
          {validationResult.error}
        </div>
      )}

      {validationResult.valid && validationResult.accountInfo && (
        <div className='flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm'>
          <CheckCircle className='h-4 w-4' />
          Connected to {validationResult.accountInfo.name} (
          {validationResult.accountInfo.id})
        </div>
      )}

      <Button
        onClick={handleCredentialsValidation}
        disabled={!credentials.secretKey || loading}
        className='w-full gap-2'
      >
        {loading ? (
          <>
            <Loader2 className='h-4 w-4 animate-spin' />
            Validating...
          </>
        ) : (
          <>
            <CheckCircle className='h-4 w-4' />
            Validate & Continue
          </>
        )}
      </Button>
    </div>
  )

  const renderWebhookStep = () => (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-semibold mb-2'>Configure Webhooks</h3>
        <p className='text-sm text-muted-foreground'>
          Set up webhook endpoints to receive real-time updates from Stripe.
        </p>
      </div>

      <div className='space-y-2'>
        <Label htmlFor='webhook-url'>Webhook Endpoint URL</Label>
        <div className='flex gap-2'>
          <div className='relative flex-1'>
            <Globe className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
            <Input
              id='webhook-url'
              value={webhookUrl}
              onChange={e => setWebhookUrl(e.target.value)}
              className='pl-10'
              readOnly
            />
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={() => copyToClipboard(webhookUrl)}
            className='gap-2'
          >
            <Copy className='h-4 w-4' />
            Copy
          </Button>
        </div>
        <p className='text-xs text-muted-foreground'>
          Add this URL to your Stripe webhook endpoints
        </p>
      </div>

      <div className='space-y-2'>
        <Label htmlFor='endpoint-secret'>Endpoint Secret</Label>
        <div className='flex gap-2'>
          <Input
            id='endpoint-secret'
            value={endpointSecret}
            readOnly
            className='font-mono text-sm'
          />
          <Button
            variant='outline'
            size='sm'
            onClick={() => copyToClipboard(endpointSecret)}
            className='gap-2'
          >
            <Copy className='h-4 w-4' />
            Copy
          </Button>
        </div>
      </div>

      <div className='space-y-3'>
        <Label>Select Events to Listen For</Label>
        <div className='grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border rounded-md p-3'>
          {STRIPE_WEBHOOK_EVENTS.map(event => (
            <div key={event} className='flex items-center space-x-2'>
              <Checkbox
                id={event}
                checked={selectedEvents.includes(event)}
                onCheckedChange={() => toggleEventSelection(event)}
              />
              <Label
                htmlFor={event}
                className='text-sm font-normal cursor-pointer flex-1'
              >
                {event}
              </Label>
            </div>
          ))}
        </div>
        <p className='text-xs text-muted-foreground'>
          {selectedEvents.length} event{selectedEvents.length !== 1 ? 's' : ''}{' '}
          selected
        </p>
      </div>

      {webhookTestResult.error && (
        <div className='flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm'>
          <AlertCircle className='h-4 w-4' />
          {webhookTestResult.error}
        </div>
      )}

      {webhookTestResult.success && (
        <div className='flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm'>
          <CheckCircle className='h-4 w-4' />
          Webhook endpoint is working correctly!
        </div>
      )}

      <div className='flex gap-3'>
        <Button
          variant='outline'
          onClick={() => setCurrentStep('credentials')}
          className='gap-2'
        >
          <ArrowLeft className='h-4 w-4' />
          Back
        </Button>
        <Button
          onClick={handleWebhookTest}
          disabled={loading || selectedEvents.length === 0}
          className='flex-1 gap-2'
        >
          {loading ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin' />
              Testing...
            </>
          ) : (
            <>
              <TestTube className='h-4 w-4' />
              Test & Continue
            </>
          )}
        </Button>
      </div>
    </div>
  )

  const renderVerificationStep = () => (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-semibold mb-2'>Complete Connection</h3>
        <p className='text-sm text-muted-foreground'>
          Review your configuration and complete the Stripe integration.
        </p>
      </div>

      <div className='space-y-4'>
        <div className='p-4 border rounded-lg space-y-3'>
          <div className='flex items-center justify-between'>
            <span className='font-medium'>Stripe Account</span>
            <Badge variant='outline'>{credentials.environment}</Badge>
          </div>
          <div className='text-sm text-muted-foreground'>
            {validationResult.accountInfo?.name} (
            {validationResult.accountInfo?.id})
          </div>
        </div>

        <div className='p-4 border rounded-lg space-y-3'>
          <div className='font-medium'>Webhook Configuration</div>
          <div className='text-sm text-muted-foreground'>
            <div>Endpoint: {webhookUrl}</div>
            <div>Events: {selectedEvents.length} selected</div>
          </div>
        </div>
      </div>

      <div className='flex gap-3'>
        <Button
          variant='outline'
          onClick={() => setCurrentStep('webhook')}
          className='gap-2'
        >
          <ArrowLeft className='h-4 w-4' />
          Back
        </Button>
        <Button
          onClick={handleCompleteConnection}
          disabled={loading}
          className='flex-1 gap-2'
        >
          {loading ? (
            <>
              <Loader2 className='h-4 w-4 animate-spin' />
              Connecting...
            </>
          ) : (
            <>
              <CheckCircle className='h-4 w-4' />
              Complete Connection
            </>
          )}
        </Button>
      </div>
    </div>
  )

  const renderCompleteStep = () => (
    <div className='text-center space-y-6'>
      <div className='mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center'>
        <CheckCircle className='h-8 w-8 text-green-600' />
      </div>
      <div>
        <h3 className='text-lg font-semibold mb-2'>Connection Successful!</h3>
        <p className='text-sm text-muted-foreground'>
          Your Stripe account has been connected successfully. You can now start
          receiving payments and managing subscriptions.
        </p>
      </div>
    </div>
  )

  return (
    <Drawer
      open={open}
      onOpenChange={newOpen => {
        setOpen(newOpen)
        if (!newOpen) {
          resetDrawer()
        }
      }}
      direction='right'
    >
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent className='h-screen w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4'>
          <DrawerTitle className='flex items-center gap-2'>
            <div className='w-6 h-6 bg-white rounded flex items-center justify-center p-1'>
              <img
                src='https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/stripe.svg'
                alt='Stripe'
                className='w-4 h-4'
                style={{ filter: 'brightness(0)' }}
              />
            </div>
            Connect Stripe
          </DrawerTitle>
          <DrawerDescription>
            Set up your Stripe integration to start accepting payments
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-6 py-6'>
          {currentStep === 'credentials' && renderCredentialsStep()}
          {currentStep === 'webhook' && renderWebhookStep()}
          {currentStep === 'verification' && renderVerificationStep()}
          {currentStep === 'complete' && renderCompleteStep()}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
